import React from 'react';
import AuthShell from './AuthShell';
import OverviewAtmosphere from './OverviewAtmosphere';
import './ChooseActionForm.css';

// The arrow that closes the ink CTA — ported from Klargo-Reference's
// StatusMark.tsx (ForwardArrow), inlined here since this page is its only
// consumer so far.
const ForwardArrow = () => (
  <svg
    width={16}
    height={12}
    viewBox="0 0 16 12"
    aria-hidden="true"
    className="ds-mark-fwd shrink-0"
    style={{ fill: 'none', stroke: 'var(--ds-ink-on)', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }}
  >
    <line x1="1" y1="6" x2="14" y2="6" />
    <polyline points="10,2 14,6 10,10" />
  </svg>
);

// Klargo Design System v4.7 — the Buy/Sell role entry (Klargo-Reference's
// RoleEntryPanels), reached right after CompleteProfile. Presentation only —
// the click handlers and everything they trigger (Formik submit for Sell,
// direct navigation for Buy) stay owned by ChooseAction.jsx.
export default function ChooseActionForm({ t, loading, onStartBuy, onStartSell, activeDealPanel = null }) {
  const panels = [
    {
      key: 'buy',
      kickerColor: 'var(--ds-buyer-fg)',
      kicker: t('chooseAction.buy.kicker'),
      title: t('chooseAction.buy.title'),
      need: t('chooseAction.buy.need'),
      cta: t('chooseAction.buy.cta'),
      ctaAria: t('chooseAction.buy.ctaAria'),
      onClick: onStartBuy,
    },
    {
      key: 'sell',
      kickerColor: 'var(--ds-seller-fg)',
      kicker: t('chooseAction.sell.kicker'),
      title: t('chooseAction.sell.title'),
      need: t('chooseAction.sell.need'),
      cta: t('chooseAction.sell.cta'),
      ctaAria: t('chooseAction.sell.ctaAria'),
      onClick: onStartSell,
    },
  ];

  return (
    <AuthShell t={t} activeKey="overview">
      <div className="choose-action relative" aria-busy={loading}>
        {!activeDealPanel && <OverviewAtmosphere />}
        <section
          className="ds-animate-page relative mx-auto max-w-[1280px] px-[clamp(18px,4vw,40px)] pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]"
          aria-label={t('chooseAction.kicker')}
          aria-busy={loading}
        >
          <div className="mb-2 text-[13px] font-medium text-ds-muted">{t('chooseAction.kicker')}</div>
          {activeDealPanel ? activeDealPanel : (
          <>
          <h1 className="ds-title-start m-0 mb-3 max-w-[640px]">{t('chooseAction.title')}</h1>
          <p className="mb-[var(--ds-lead-gap)] max-w-[620px] text-base leading-[1.55] text-ds-text">
            {t('chooseAction.subtitle')}
          </p>

          <div className="flex max-w-[560px] flex-col gap-[18px] md:grid md:max-w-[900px] md:grid-cols-2 md:items-stretch md:gap-5">
            {panels.map((panel) => (
              <div
                key={panel.key}
                className={`choose-action-panel choose-action-panel--${panel.key === 'buy' ? 'buyer' : 'seller'} flex min-w-0 flex-none flex-col rounded-xl border border-ds-sheet-edge bg-ds-sheet px-[26px] pb-[26px] pt-[23px]`}
                style={{ boxShadow: '0 1px 2px rgba(16,30,40,.05)' }}
              >
                <span className="text-[13px] font-semibold tracking-[0.01em]" style={{ color: panel.kickerColor }}>
                  {panel.kicker}
                </span>
                <h2 className="ds-title-section mb-2.5 mt-[13px] text-2xl leading-[1.15]">{panel.title}</h2>
                <p className="ds-body mb-[22px] max-w-[340px]">
                  <span className="font-semibold text-ds-body">{t('chooseAction.needLabel')}</span> {panel.need}
                </p>
                <button
                  type="button"
                  onClick={panel.onClick}
                  aria-label={panel.ctaAria}
                  disabled={loading}
                  className="ds-action-ink mt-auto self-start"
                >
                  <span>{panel.cta}</span>
                  <ForwardArrow />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-[var(--ds-block-gap)] max-w-[900px]">
            <h2 className="ds-title-component m-0 mb-1.5">{t('chooseAction.process.title')}</h2>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-x-9 border-t border-[rgba(20,36,46,.22)]">
              {['01', '02', '03'].map((step) => (
                <div key={step} className="flex gap-3 pb-1.5 pt-3.5">
                  <span className="ds-mono flex-none pt-0.5 text-[13px] text-ds-muted">{step}</span>
                  <span>
                    <span className="block text-[15px] font-semibold text-ds-ink">
                      {t(`chooseAction.process.${step}.title`)}
                    </span>
                    <span className="ds-body mt-[3px] block text-sm">
                      {t(`chooseAction.process.${step}.body`)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          </>
          )}
        </section>
      </div>
    </AuthShell>
  );
}
